"use client";

import { useState } from "react";
import toast from "react-hot-toast";
import IconArrowRightUp from "@/components/icons/icon-arrow-right-up";

type CartLine = {
  id: number;
  name: string;
  price: number;
  imageUrl?: string;
  quantity: number;
  selected: boolean;
};

type Props = {
  item: { id: number; name: string; price: number; imageUrl?: string };
  qty?: number;
  className?: string;
};

const KEY = "majlis:cart:v1";

function readCart(): CartLine[] {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage.getItem(KEY);
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw) as unknown[];
    return Array.isArray(arr)
      ? arr
          .map((x) => ({
            id: Number((x as any)?.id),
            name: String((x as any)?.name ?? ""),
            price: Number((x as any)?.price ?? 0),
            imageUrl: (x as any)?.imageUrl
              ? String((x as any)?.imageUrl)
              : undefined,
            quantity: Math.max(1, Number((x as any)?.quantity ?? 1)),
            selected: Boolean((x as any)?.selected ?? true),
          }))
          .filter((l) => Number.isFinite(l.id) && l.name)
      : [];
  } catch {
    return [];
  }
}

function writeCart(lines: CartLine[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(lines));
  window.dispatchEvent(new Event("cartUpdated"));
}

export default function AddToCartButton({ item, qty = 1, className }: Props) {
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setLoading(true);
    const lines = readCart();
    const i = lines.findIndex((l) => l.id === item.id);
    if (i >= 0) {
      lines[i] = {
        ...lines[i],
        quantity: lines[i].quantity + qty,
        selected: true,
      };
    } else {
      lines.unshift({
        id: item.id,
        name: item.name,
        price: Number(item.price),
        imageUrl: item.imageUrl,
        quantity: qty,
        selected: true,
      });
    }
    writeCart(lines);
    toast.success("Added to cart");
    setTimeout(() => setLoading(false), 200);
  };

  return (
    <button
      onClick={handleAdd}
      disabled={loading}
      className={
        className ??
        "sm:max-md:max-w-[368px] w-full center h-10 sm:h-12 xl:h-[52px] 2xl:h-[67px] px-[35px] border border-white bg-white/[1%] backdrop-blur-[23.55px] text-white text-xs sm:text-lg 2xl:text-[22px] rounded-[25px] flex items-center gap-2 overflow-hidden"
      }
    >
      {loading ? "Adding..." : "Add to cart"}
      <IconArrowRightUp className="2xl:w-[19px] sm:w-[16px] w-[12px]" />
    </button>
  );
}
